import apiCall from '../Redux/ApiCalls/AuthorizedApi'
import store from '../Redux/Store'
import {
  addToCompressingFolder,
  updateTotalProgress,
} from '../Redux/Slice/Dashboard/AlbumSlice'
import {
  countUploadedFilesFromBatches,
  createUploadJob,
  deleteUploadJob,
  filesToPersisted,
  getBatch,
  getJobMeta,
  markBatchUploaded,
  markBatchUploading,
  persistedToFiles,
  saveBatch,
  updateJobProgress,
  type PersistedBatch,
} from './uploadJobPersistence'
import { showErrorToast } from '../components/atoms/Utlis/Toast'
import type {
  FailedUploadDetail,
  FolderUploadPipelineParams,
  FolderUploadPipelineResult,
} from './folderImageUploadTypes'
import { ENABLE_UPLOAD_RESUME_ON_REFRESH } from './folderUploadConfig'
import {
  fetchExistingFolderFileNames,
  normalizeUploadFileName,
  partitionFilesByExisting,
} from './folderUploadDedup'

function chunkFiles(files: File[], size: number): File[][] {
  const chunks: File[][] = []
  for (let i = 0; i < files.length; i += size) {
    chunks.push(files.slice(i, i + size))
  }
  return chunks
}

/** Images per compress + upload cycle (API limit). */
export const UPLOAD_BATCH_SIZE = 15
/**
 * Run up to 3 concurrent upload pipelines.  The old value of 1 was overly
 * cautious — the server-side deadlock issue was fixed; keeping some
 * concurrency dramatically speeds up bulk uploads.
 */
export const PARALLEL_PIPELINES = 3

/** Max attempts per batch: 1 initial attempt + 1 retry only if rejected. */
const MAX_UPLOAD_RETRIES = 2
const RETRY_BASE_MS = 1000
/** Pause between successful batches to reduce DB lock contention. */
const DELAY_BETWEEN_BATCHES_MS = 100
/** Per-batch upload timeout in milliseconds (5 minutes). */
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000

type PendingFailedBatch = {
  images: File[]
  compression_percentage: number
  batchIndex: number
  jobId: string
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** 
 * Every rejected batch (including server 500, image identify errors, deadlocks, timeouts, etc.)
 * gets allowed exactly 1 more retry before skipping to the next batch.
 */
export function isRetryableUploadError(error: unknown): boolean {
  return true
}

/** Compress one batch in a short-lived worker (terminated after each batch). */
export function compressFilesInWorker(
  files: File[],
  compressionRate: number,
  onFileComplete?: (completedInBatch: number, batchSize: number) => void,
): Promise<File[]> {
  if (Number(compressionRate) <= 0) {
    return Promise.resolve(files)
  }
  const ratio = Math.max(10, 100 - compressionRate)
  return new Promise((resolve, reject) => {
    const worker = new Worker(
      new URL(
        '../components/molecules/Dashboard/SingleAlbumPage/myWorker.worker.ts',
        import.meta.url,
      ),
    )
    let completedInBatch = 0
    const failedFiles: Array<{
      fileName: string
      fileType?: string
      fileSize?: number
      error: string
      fallbackUploadedOriginal?: boolean
    }> = []
    worker.onmessage = (e: MessageEvent) => {
      if (e.data.type === 'progress') {
        completedInBatch += Number(e.data.completedFiles) || 1
        onFileComplete?.(completedInBatch, files.length)
        return
      }
      if (e.data.type === 'file-error') {
        failedFiles.push({
          fileName: String(e.data.fileName || 'unknown-file'),
          fileType: e.data.fileType ? String(e.data.fileType) : undefined,
          fileSize:
            typeof e.data.fileSize === 'number' ? e.data.fileSize : undefined,
          error: String(e.data.error || 'Unknown compression error'),
          fallbackUploadedOriginal: Boolean(e.data.fallbackUploadedOriginal),
        })
        return
      }
      if (e.data.type === 'complete') {
        worker.terminate()
        const result = (e.data.files as File[]).filter(
          (f): f is File => f !== undefined,
        )
        if (failedFiles.length > 0) {
          console.error(
            '[folder-upload] Compression failed for file(s):',
            failedFiles,
          )
        }
        resolve(result)
      }
    }
    worker.onerror = (err) => {
      worker.terminate()
      reject(err)
    }
    worker.postMessage({ files, work: 0, ratio })
  })
}

function clampProgress(pct: number): number {
  if (!Number.isFinite(pct) || pct <= 0) return 0.01
  if (pct >= 100) return 99.99
  return pct
}

export function extractAndNotifyFailedUploads(response: any): {
  failedCount: number
  failedFiles: Set<string>
  failedDetails: FailedUploadDetail[]
} {
  const payload = response?.data ?? response
  const failedDetails: FailedUploadDetail[] = []
  const failedFiles = new Set<string>()

  // 1. Process failed_upload_details array (e.g. [{ file: "equipment.svg", error: "SVG is not supported..." }])
  const details = payload?.failed_upload_details
  if (Array.isArray(details) && details.length > 0) {
    for (const item of details) {
      const fileName = String(item?.file ?? item?.file_name ?? '').trim()
      const errorMsg = String(item?.error ?? item?.reason ?? 'Upload failed').trim()
      failedDetails.push({ file: fileName, error: errorMsg })
      if (fileName) {
        failedFiles.add(normalizeUploadFileName(fileName))
      }
      const message =
        fileName && !errorMsg.toLowerCase().startsWith(fileName.toLowerCase())
          ? `${fileName}: ${errorMsg}`
          : errorMsg
      showErrorToast(message)
    }
  } else {
    // 2. Fallback to failed_alerts if failed_upload_details is absent
    const alerts = payload?.upload_messages?.failed_alerts
    if (Array.isArray(alerts) && alerts.length > 0) {
      for (const alertMsg of alerts) {
        if (typeof alertMsg === 'string' && alertMsg.trim()) {
          showErrorToast(alertMsg.trim())
        }
      }
    }

    // 3. Fallback to failed_to_upload
    const failedList = payload?.failed_to_upload
    if (Array.isArray(failedList) && failedList.length > 0) {
      for (const item of failedList) {
        const fileName = typeof item === 'string' ? item : item?.file || 'Image'
        if (fileName) {
          failedFiles.add(normalizeUploadFileName(fileName))
          if (!alerts || alerts.length === 0) {
            showErrorToast(`${fileName} failed to upload.`)
          }
        }
      }
    }
  }

  let failedCount = failedDetails.length
  if (failedCount === 0 && failedFiles.size > 0) {
    failedCount = failedFiles.size
  }
  if (failedCount === 0 && typeof payload?.upload_messages?.failed_count === 'number') {
    failedCount = payload.upload_messages.failed_count
  }

  return { failedCount, failedFiles, failedDetails }
}

async function uploadImageBatch(params: {
  id: number
  project_id: number
  folder_name: string
  images: File[]
  compression_percentage: number
  onUploadProgress?: (fraction: number) => void
}): Promise<any> {
  const formData = new FormData()
  formData.append('folder_name', params.folder_name)
  formData.append('folder_id', `${params.id}`)
  formData.append('project_id', `${params.project_id}`)
  const compressionPercentage = params.compression_percentage ?? 25
  params.images.forEach((image, index) => {
    formData.append(`media[${index}][image]`, image)
    formData.append(`media[${index}][media_type]`, '1')
    formData.append(
      `media[${index}][compression_percentage]`,
      `${compressionPercentage}`,
    )
  })
  const response = await apiCall({
    method: 'POST',
    url: '/project/user/upload-image/',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: formData,
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: (event) => {
      if (!params.onUploadProgress) return
      const total = event.total
      if (!total || total <= 0) return
      params.onUploadProgress(Math.min(1, event.loaded / total))
    },
  })
  return response
}

async function uploadImageBatchWithRetry(
  params: {
    id: number
    project_id: number
    folder_name: string
    images: File[]
    compression_percentage: number
    onUploadProgress?: (fraction: number) => void
  },
  maxRetries = MAX_UPLOAD_RETRIES,
): Promise<any> {
  let lastError: unknown
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await uploadImageBatch(params)
      return response
    } catch (error: any) {
      lastError = error
      const canRetry =
        isRetryableUploadError(error) && attempt < maxRetries - 1
      if (!canRetry) {
        if (error?.response?.data) {
          const notified = extractAndNotifyFailedUploads(error.response.data)
          if (notified.failedDetails.length > 0 || notified.failedCount > 0) {
            error.hasShownToast = true
          }
        }
        throw error
      }
      const delay =
        RETRY_BASE_MS * Math.pow(2, attempt) + Math.random() * 400
      await sleep(delay)
    }
  }
  throw lastError
}

type PipelineContext = {
  jobId: string
  id: number
  project_id: number
  folder_name: string
  compressionRate: number
  total: number
}

async function runPersistedJob(
  jobId: string,
): Promise<FolderUploadPipelineResult> {
  const meta = await getJobMeta(jobId)
  if (!meta) {
    throw new Error('Upload job not found.')
  }

  const ctx: PipelineContext = {
    jobId,
    id: meta.folderId,
    project_id: meta.projectId,
    folder_name: meta.folderName,
    compressionRate: meta.compressionPercentage,
    total: meta.totalFiles,
  }

  let uploadDone = await countUploadedFilesFromBatches(jobId, meta.batchCount)
  if (uploadDone !== meta.uploadedFiles) {
    await updateJobProgress(jobId, uploadDone)
  }
  let compressDone = uploadDone
  let skippedDuplicates = 0
  let partialFailedCount = 0
  const allFailedDetails: FailedUploadDetail[] = []
  const failedBatches: PendingFailedBatch[] = []
  let existingNames = await fetchExistingFolderFileNames(ctx.id)

  const shouldCompress = Number(ctx.compressionRate) > 0

  const reportCompress = (extraCompleted = 0) => {
    if (!shouldCompress) return
    const done = Math.min(ctx.total, compressDone + extraCompleted)
    const pct = ctx.total > 0 ? (done / ctx.total) * 100 : 100
    store.dispatch(
      addToCompressingFolder({
        folderId: ctx.id,
        folderName: ctx.folder_name,
        totalProgress: clampProgress(pct),
      }),
    )
  }

  const reportUpload = async (batchSize = 0, batchFraction = 0) => {
    const fractional =
      ctx.total > 0 ? (batchSize / ctx.total) * batchFraction * 100 : 0
    const pct =
      ctx.total > 0 ? ((uploadDone + partialFailedCount) / ctx.total) * 100 + fractional : 100
    store.dispatch(
      updateTotalProgress({
        folderId: ctx.id,
        folderName: ctx.folder_name,
        totalProgress: clampProgress(pct),
      }),
    )
    if (batchFraction === 0 || batchFraction >= 1) {
      await updateJobProgress(jobId, uploadDone)
    }
  }

  store.dispatch(
    updateTotalProgress({
      folderId: ctx.id,
      folderName: ctx.folder_name,
      totalProgress: clampProgress(((uploadDone + partialFailedCount) / ctx.total) * 100),
    }),
  )
  if (shouldCompress) {
    reportCompress()
  }

  const uploadBatch = async (
    images: File[],
    compression_percentage: number,
    batchIndex: number,
    persistedRecord?: PersistedBatch,
  ): Promise<boolean> => {
    try {
      const { toUpload, skippedAsDuplicate } = partitionFilesByExisting(
        images,
        existingNames,
      )
      skippedDuplicates += skippedAsDuplicate.length

      const handledCount = images.length

      if (toUpload.length === 0) {
        if (persistedRecord) {
          uploadDone = await markBatchUploaded(
            jobId,
            batchIndex,
            handledCount,
          )
        } else {
          uploadDone += handledCount
        }
        await reportUpload()
        return true
      }

      if (persistedRecord) {
        await markBatchUploading(jobId, {
          ...persistedRecord,
          fileCount: persistedRecord.fileCount || toUpload.length,
          files:
            persistedRecord.files.length > 0
              ? filesToPersisted(toUpload)
              : persistedRecord.files,
        })
      }

      const batchResult = await uploadImageBatchWithRetry({
        id: ctx.id,
        project_id: ctx.project_id,
        folder_name: ctx.folder_name,
        images: toUpload,
        compression_percentage,
        onUploadProgress: (fraction) => {
          void reportUpload(toUpload.length, fraction)
        },
      })

      const { failedCount, failedFiles, failedDetails } =
        extractAndNotifyFailedUploads(batchResult)
      partialFailedCount += failedCount
      if (failedDetails.length > 0) {
        allFailedDetails.push(...failedDetails)
      }

      for (const file of toUpload) {
        if (!failedFiles.has(normalizeUploadFileName(file.name))) {
          existingNames.add(normalizeUploadFileName(file.name))
        }
      }

      const batchUploadedCount = Math.max(0, toUpload.length - failedCount)
      const batchSavedCount = batchUploadedCount + skippedAsDuplicate.length

      if (persistedRecord) {
        uploadDone = await markBatchUploaded(jobId, batchIndex, batchSavedCount)
      } else {
        uploadDone += batchSavedCount
      }
      await reportUpload()
      await sleep(DELAY_BETWEEN_BATCHES_MS)
      return true
    } catch {
      failedBatches.push({
        images,
        compression_percentage,
        batchIndex,
        jobId,
      })
      const failedRecord: PersistedBatch = {
        batchIndex,
        status: 'failed',
        files: filesToPersisted(images),
        fileCount: images.length,
      }
      await saveBatch(jobId, failedRecord)
      return false
    }
  }

  type PreparedBatch = {
    index: number
    filesToUpload: File[]
    recordForUpload: PersistedBatch
  }

  const prepareBatch = async (i: number): Promise<PreparedBatch | null> => {
    const batch = await getBatch(jobId, i)
    // Never re-upload batches that are already done OR previously failed
    if (!batch || batch.status === 'done' || batch.status === 'failed') return null

    if (batch.status === 'uploading' && batch.files.length > 0) {
      const filesToUpload = persistedToFiles(batch.files)
      if (shouldCompress) {
        compressDone += filesToUpload.length
        reportCompress()
      }
      return { index: i, filesToUpload, recordForUpload: batch }
    }

    if (batch.status === 'compressed') {
      const filesToUpload = persistedToFiles(batch.files)
      if (shouldCompress) {
        compressDone += filesToUpload.length
        reportCompress()
      }
      return { index: i, filesToUpload, recordForUpload: batch }
    }

    const rawFiles = persistedToFiles(batch.files)
    if (!shouldCompress) {
      const recordForUpload: PersistedBatch = {
        batchIndex: i,
        status: 'compressed',
        files: filesToPersisted(rawFiles),
        fileCount: rawFiles.length,
      }
      await saveBatch(jobId, recordForUpload)
      return { index: i, filesToUpload: rawFiles, recordForUpload }
    }

    const compressed = await compressFilesInWorker(
      rawFiles,
      ctx.compressionRate,
      (completedInBatch) => reportCompress(completedInBatch),
    )
    compressDone += rawFiles.length
    reportCompress()

    const recordForUpload: PersistedBatch = {
      batchIndex: i,
      status: 'compressed',
      files: filesToPersisted(compressed),
      fileCount: compressed.length,
    }
    await saveBatch(jobId, recordForUpload)
    return { index: i, filesToUpload: compressed, recordForUpload }
  }

  let nextPrepared: Promise<PreparedBatch | null> | null = null
  const inflight: Set<Promise<void>> = new Set()

  for (let i = 0; i < meta.batchCount; i++) {
    const prepared =
      (await nextPrepared) ?? (await prepareBatch(i))
    nextPrepared = null

    if (!prepared) continue

    // Overlap: compress next batch while this one uploads
    if (i + 1 < meta.batchCount) {
      nextPrepared = prepareBatch(i + 1)
    }

    // Wait if we've hit the concurrency limit
    if (inflight.size >= PARALLEL_PIPELINES) {
      await Promise.race(inflight)
    }

    const p = prepared
    const task = uploadBatch(
      p.filesToUpload,
      ctx.compressionRate,
      p.index,
      p.recordForUpload,
    ).then(
      () => { inflight.delete(task) },
      () => { inflight.delete(task) },
    )
    inflight.add(task)
  }

  // Wait for all remaining uploads to complete
  if (inflight.size > 0) {
    await Promise.allSettled(inflight)
  }

  const failedBatchesCount = failedBatches.reduce((n, b) => n + b.images.length, 0)
  const totalFailed = failedBatchesCount + partialFailedCount
  const uploaded = Math.max(0, uploadDone - skippedDuplicates)

  // Always delete the job once all batches have been processed so that on
  // refresh or hard refresh, rejected/failed batches are NOT retried again.
  await deleteUploadJob(jobId)

  if (totalFailed > 0 && uploaded === 0) {
    const err = new Error(
      'Upload failed. Please wait a moment and try again.',
    ) as Error & {
      pipelineResult?: FolderUploadPipelineResult
      hasShownToast?: boolean
    }
    err.pipelineResult = {
      uploaded,
      failed: totalFailed,
      total: ctx.total,
      folderId: ctx.id,
      projectId: ctx.project_id,
      failedUploadDetails: allFailedDetails,
    }
    err.hasShownToast = allFailedDetails.length > 0
    throw err
  }

  return {
    uploaded,
    failed: totalFailed,
    total: ctx.total,
    folderId: ctx.id,
    projectId: ctx.project_id,
    skippedDuplicates,
    failedUploadDetails: allFailedDetails,
  }
}

export async function resumeFolderImageUploadJob(
  jobId: string,
): Promise<FolderUploadPipelineResult> {
  if (!ENABLE_UPLOAD_RESUME_ON_REFRESH) {
    // throw new Error('Upload resume after refresh is disabled.')
  }
  return runPersistedJob(jobId)
}

/** Upload without IndexedDB (used when refresh-resume is disabled). */
async function runInMemoryUploadPipeline(
  params: FolderUploadPipelineParams,
): Promise<FolderUploadPipelineResult> {
  const compressionRate = Number(params.compression_percentage ?? 25)
  const shouldCompress = compressionRate > 0
  const fileBatches = chunkFiles(params.folderImages, UPLOAD_BATCH_SIZE)
  const total = params.folderImages.length

  let uploadDone = 0
  let compressDone = 0
  let skippedDuplicates = 0
  let partialFailedCount = 0
  const allFailedDetails: FailedUploadDetail[] = []
  const failedBatches: PendingFailedBatch[] = []
  let existingNames = await fetchExistingFolderFileNames(params.id)

  const reportCompress = (extraCompleted = 0) => {
    if (!shouldCompress) return
    const done = Math.min(total, compressDone + extraCompleted)
    const pct = total > 0 ? (done / total) * 100 : 100
    store.dispatch(
      addToCompressingFolder({
        folderId: params.id,
        folderName: params.folder_name,
        totalProgress: clampProgress(pct),
      }),
    )
  }

  const reportUpload = (batchSize = 0, batchFraction = 0) => {
    const fractional =
      total > 0 ? (batchSize / total) * batchFraction * 100 : 0
    const pct = total > 0 ? ((uploadDone + partialFailedCount) / total) * 100 + fractional : 100
    store.dispatch(
      updateTotalProgress({
        folderId: params.id,
        folderName: params.folder_name,
        totalProgress: clampProgress(pct),
      }),
    )
  }

  store.dispatch(
    updateTotalProgress({
      folderId: params.id,
      folderName: params.folder_name,
      totalProgress: 0.01,
    }),
  )
  if (shouldCompress) {
    reportCompress()
  }

  const uploadBatch = async (
    images: File[],
    compression_percentage: number,
    batchIndex: number,
  ): Promise<boolean> => {
    try {
      const { toUpload, skippedAsDuplicate } = partitionFilesByExisting(
        images,
        existingNames,
      )
      skippedDuplicates += skippedAsDuplicate.length

      if (toUpload.length === 0) {
        uploadDone += images.length
        reportUpload()
        return true
      }

      const batchResult = await uploadImageBatchWithRetry({
        id: params.id,
        project_id: params.project_id,
        folder_name: params.folder_name,
        images: toUpload,
        compression_percentage,
        onUploadProgress: (fraction) => {
          reportUpload(toUpload.length, fraction)
        },
      })

      const { failedCount, failedFiles, failedDetails } =
        extractAndNotifyFailedUploads(batchResult)
      partialFailedCount += failedCount
      if (failedDetails.length > 0) {
        allFailedDetails.push(...failedDetails)
      }

      for (const file of toUpload) {
        if (!failedFiles.has(normalizeUploadFileName(file.name))) {
          existingNames.add(normalizeUploadFileName(file.name))
        }
      }

      const batchUploadedCount = Math.max(0, toUpload.length - failedCount)
      uploadDone += batchUploadedCount + skippedAsDuplicate.length
      reportUpload()
      await sleep(DELAY_BETWEEN_BATCHES_MS)
      return true
    } catch {
      failedBatches.push({
        images,
        compression_percentage,
        batchIndex,
        jobId: 'memory',
      })
      return false
    }
  }

  let nextCompressed: Promise<File[]> | null = null

  // Process batches with bounded concurrency for faster uploads
  const inflight: Set<Promise<void>> = new Set()

  for (let i = 0; i < fileBatches.length; i++) {
    const rawBatch = fileBatches[i]
    let filesToUpload: File[]

    if (!shouldCompress) {
      filesToUpload = rawBatch
    } else {
      const compressed =
        (await nextCompressed) ??
        (await compressFilesInWorker(
          rawBatch,
          compressionRate,
          (completedInBatch) => reportCompress(completedInBatch),
        ))
      nextCompressed = null
      compressDone += rawBatch.length
      reportCompress()

      if (i + 1 < fileBatches.length) {
        const nextRaw = fileBatches[i + 1]
        nextCompressed = compressFilesInWorker(
          nextRaw,
          compressionRate,
          (completedInBatch) => reportCompress(completedInBatch),
        )
      }
      filesToUpload = compressed
    }

    // Wait if we've hit the concurrency limit
    if (inflight.size >= PARALLEL_PIPELINES) {
      await Promise.race(inflight)
    }

    const batchIdx = i
    const uploadFiles = filesToUpload
    const task = uploadBatch(uploadFiles, compressionRate, batchIdx).then(
      () => { inflight.delete(task) },
      () => { inflight.delete(task) },
    )
    inflight.add(task)
  }

  // Wait for all remaining uploads to complete
  if (inflight.size > 0) {
    await Promise.allSettled(inflight)
  }

  const failedBatchesCount = failedBatches.reduce((n, b) => n + b.images.length, 0)
  const totalFailed = failedBatchesCount + partialFailedCount
  const uploaded = Math.max(0, uploadDone - skippedDuplicates)

  if (totalFailed > 0 && uploaded === 0) {
    const err = new Error(
      'Upload failed. Please try again.',
    ) as Error & {
      pipelineResult?: FolderUploadPipelineResult
      hasShownToast?: boolean
    }
    err.pipelineResult = {
      uploaded,
      failed: totalFailed,
      total,
      folderId: params.id,
      projectId: params.project_id,
      failedUploadDetails: allFailedDetails,
    }
    err.hasShownToast = allFailedDetails.length > 0
    throw err
  }

  return {
    uploaded,
    failed: totalFailed,
    total,
    folderId: params.id,
    projectId: params.project_id,
    skippedDuplicates,
    failedUploadDetails: allFailedDetails,
  }
}

export async function runFolderImageUploadPipeline(
  params: FolderUploadPipelineParams,
): Promise<FolderUploadPipelineResult> {
  const compressionRate = Number(params.compression_percentage ?? 25)
  const shouldCompress = compressionRate > 0

  store.dispatch(
    updateTotalProgress({
      folderId: params.id,
      folderName: params.folder_name,
      totalProgress: 0.01,
    }),
  )
  if (shouldCompress) {
    store.dispatch(
      addToCompressingFolder({
        folderId: params.id,
        folderName: params.folder_name,
        totalProgress: 0.01,
      }),
    )
  }

  if (!ENABLE_UPLOAD_RESUME_ON_REFRESH) {
    return runInMemoryUploadPipeline(params)
  }

  const jobId = await createUploadJob({
    folderId: params.id,
    projectId: params.project_id,
    folderName: params.folder_name,
    compressionPercentage: compressionRate,
    files: params.folderImages,
  })

  return runPersistedJob(jobId)
}
